<script lang="ts">
	import { page } from "$app/state";
	import { onMount } from "svelte";
	import Card from "./Card.svelte";
	import { breakpoints as br } from "./utils/getBreakpoints";
	import type { Product } from "./types/products";
	import { fade } from "svelte/transition";

	let search = $state("");
	onMount(() => {
		search = page.url.search;
	});

	interface Props {
		products: Readonly<Product[]>;
		horizontalScroll?: boolean;
	}

	let { products, horizontalScroll = false }: Props = $props();
</script>

<div class:horizontal-scroll={horizontalScroll}>
	{#if !products}
		<p class="mx-edge-x">Feil ved lasting av produkter</p>
	{:else}
		<ul class="products">
			{#each products as product, i (product.slug.current)}
				{@const slug = product.slug?.current?.toLowerCase()}

				{#if !product.not_available && !product.unlisted && slug}
					<li class="block relative isolate" in:fade|global={{ duration: 300 }}>
						{#if product.energy_certificate}
							<div class="absolute top-0 left-0 z-1 w-20">
								<img
									src={`/_assets/energy-certificates/${product.energy_certificate}.svg`}
									alt={`${product.energy_certificate}`}
								/>
							</div>
						{/if}
						<Card
							rel={search ? "nofollow" : undefined}
							href={`/nettbutikk/${slug}${search}`}
							pictureObject={product.images[0]}
							imgSizes={`(min-width: ${br.desktop}) 20vw, (min-width: ${br.landscape}) 45vw, 90vw`}
							title={product.title}
							buttonArrow={false}
							hrefLabel="Les mer og bestill"
							pictureLoading={i > 0 ? "lazy" : "eager"}
						>
							<p class="m-0">{product.description_short || product.description_title}</p>
							<p class="mb-0 text-h2">
								{#if product.down_payment && product.additional_category === "elektriker"}
									<strong class="text-[--lysning]">Inntil 30 000 kr</strong>
								{:else if product.down_payment}
									<strong class="text-[--lysning]"
										>{(product.price / (product.down_payment * 12)).toFixed(0)},- i mnd</strong
									>
								{:else}
									<strong class="text-[--lysning]"
										>{product.price.toLocaleString("nb-NO")} kr</strong
									>
								{/if}
							</p>
							{#if product.down_payment && product.additional_category === "elektriker"}
								<p class="m-0">Delbetaling over strømregninga i inntil 3 år</p>
							{:else if product.down_payment}
								<p class="m-0">Totalpris: {product.price.toLocaleString("nb-NO")} kr</p>
							{/if}
						</Card>
					</li>
				{/if}
			{/each}
		</ul>
	{/if}
</div>

<style lang="postcss">
	.products {
		gap: var(--s-16) var(--gap-inline);
		list-style: none;
		padding: 0;
	}

	div:not(.horizontal-scroll) .products {
		display: grid;
	}

	@media (--landscape) {
		div:not(.horizontal-scroll) .products {
			grid-template-columns: repeat(var(--product-cols, 2), minmax(0, 1fr));
		}
	}

	@media (--desktop) {
		div:not(.horizontal-scroll) .products {
			--product-cols: 3;
		}
	}

	/* Horizontal scroll specific styling */

	div.horizontal-scroll {
		--card-text-height: 10.5em;
		--_w-card-adjust: var(--gap-inline);
		--_w-card: calc(var(--w-col) * var(--card-span-cols, 2) - var(--_w-card-adjust));
		--_w-edge-x: calc(var(--space-edge-x) - var(--gap-inline));

		min-height: calc(var(--card-text-height) + var(--_w-card) / 16 * 9 + 6.5rem);
	}

	div.horizontal-scroll .products {
		display: flex;
		overflow-x: auto;
		padding-block: 3px var(--space); /* padding makes room for outline and horizontal-scroll bar */
		scroll-snap-type: x mandatory;
		scroll-padding-inline: var(--space-edge-x);
	}

	div.horizontal-scroll .products > :global(*) {
		display: block;
		scroll-snap-align: start;
		min-width: var(--_w-card);
	}

	div.horizontal-scroll .products::before,
	div.horizontal-scroll .products::after {
		content: "";
		display: block;
		width: var(--_w-edge-x);
	}

	@media (--landscape) {
		div.horizontal-scroll {
			--card-span-cols: 4;
		}
	}

	@media (--tablet) {
		div.horizontal-scroll {
			--_w-card-adjust: 0;
			--card-span-cols: 2;

			min-height: calc(
				var(--card-text-height) + (100vw - 2 * var(--space-edge-x) - 2 * var(--gap-inline)) / 3 /
					16 * 9 + 5rem
			);
		}

		div.horizontal-scroll .products {
			display: grid;
			grid-template-columns: var(--_w-edge-x) 1fr 1fr 1fr var(--_w-edge-x);
			padding-block-end: 3px; /* padding makes room for outline, but not horizontal-scroll bar anymore */
		}
	}
</style>
